import AnchorLink, {
  AnchorLinkProps
} from "@bmi-digital/components/anchor-link";
import LeadBlock from "@bmi-digital/components/lead-block";
import Section from "@bmi-digital/components/section";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { graphql } from "gatsby";
import React from "react";
import { replaceSpaces } from "@bmi-digital/components/utils";
import RichText, { parseReachDataRawFields } from "./RichText";
import { useSiteContext } from "./Site";
import ButtonLink from "./link/ButtonLink";
import {
  LeadBlockWrapper,
  LinksContainer,
  LinkWrapper,
  Text
} from "./styles/leadBlockSectionStyles";
import type { RichTextData } from "./RichText";
import type { PayloadLeadBlockSection } from "../types/payload";
import type { LinkData } from "./link/types";

export type LeadBlockData = {
  richTextDescription: RichTextData;
  link: readonly [LinkData] | null;
  postItCard: RichTextData | null;
};

export type LeadBlockSectionData = Pick<
  PayloadLeadBlockSection,
  "blockType" | "title"
> &
  LeadBlockData;

const LeadBlockSection = ({
  data: { richTextDescription, link, postItCard }
}: {
  data: LeadBlockData;
}) => {
  const { getMicroCopy } = useSiteContext();

  return (
    <Section
      backgroundColor="white"
      spacing="none"
      data-testid={`lead-block-section`}
    >
      <LeadBlockWrapper>
        <LeadBlock.Content>
          <Text>
            <RichText
              document={richTextDescription}
              underlineHeadings={["h2", "h3", "h4"]}
              hasNoBottomMargin
            />
          </Text>
          {link && link[0] && (
            <LinkWrapper>
              <ButtonLink
                data={link[0]}
                variant="opaqueOutlined"
                data-testid={`anchor-link-${replaceSpaces(link[0].label)}`}
              >
                {link[0].label}
              </ButtonLink>
            </LinkWrapper>
          )}
        </LeadBlock.Content>
        <LeadBlock.Card color="pearl" data-testid="lead-block-card">
          <LeadBlock.Card.Section>
            {postItCard ? (
              <RichText
                document={postItCard}
                underlineHeadings={["h2", "h3", "h4"]}
                gtmLabel={
                  parseReachDataRawFields(postItCard)["h4"] ||
                  parseReachDataRawFields(postItCard)["h5"]
                }
              />
            ) : (
              <>
                <LeadBlock.Card.Heading hasUnderline>
                  {getMicroCopy(microCopy.PAGE_JUMP_TO_SECTION)}
                </LeadBlock.Card.Heading>
                <LeadBlock.Card.Content>
                  <LinksContainer
                    anchorLinkComponent={(props: AnchorLinkProps) => (
                      <AnchorLink
                        {...props}
                        gtm={{
                          id: "cta-click1",
                          label: `${getMicroCopy(
                            microCopy.PAGE_JUMP_TO_SECTION
                          )} - ${props.children}`,
                          action: props.href
                        }}
                        data-testid={`anchor-link-${replaceSpaces(
                          getMicroCopy(microCopy.PAGE_JUMP_TO_SECTION)
                        )}`}
                      />
                    )}
                  />
                </LeadBlock.Card.Content>
              </>
            )}
          </LeadBlock.Card.Section>
        </LeadBlock.Card>
      </LeadBlockWrapper>
    </Section>
  );
};

export default LeadBlockSection;

export const query = graphql`
  fragment LeadBlockFragment on PayloadLeadBlockSection {
    title
    richTextDescription: description {
      ...RichTextFragment
    }
    link {
      ...LinkFragment
    }
    postItCard {
      ...RichTextFragment
    }
  }

  fragment LeadBlockSectionFragment on PayloadLeadBlockSection {
    blockType
    ...LeadBlockFragment
  }
`;
